import { updateCoffeeShipping } from './bbx-coffee-shipping.js';

/**
 * Derives the Shopify form state for the subscription selector.
 *
 * @param {{
 *   purchaseType: 'once' | 'subscribe',
 *   grindConfirmed: boolean,
 *   bagCount: number,
 *   planId: string,
 *   oncePrice: string,
 *   plans: Array<{
 *     id: string,
 *     oneBagPrice: string,
 *     twoBagPrice: string,
 *     threeBagPrice: string,
 *     fourBagPrice: string,
 *     savingPercent: number
 *   }>
 * }} input
 */
export function deriveSubscriptionState(input) {
  const missingSelections = [];
  if (!input.grindConfirmed) missingSelections.push('grind');

  const validOnceQuantity = Number.isInteger(input.bagCount) && input.bagCount >= 1 && input.bagCount <= 4;
  if (input.purchaseType === 'once') {
    if (!validOnceQuantity) missingSelections.push('quantity');
    return {
      sellingPlanId: '',
      quantity: validOnceQuantity ? input.bagCount : 0,
      buttonText: missingSelections.length ? 'Confirm your grind' : 'Add one time purchase',
      deliveryPrice: ({1: input.oncePrice, 2: input.onceTwoPrice, 3: input.onceThreePrice, 4: input.onceFourPrice})[input.bagCount] || input.oncePrice,
      savingPercent: 0,
      isComplete: missingSelections.length === 0,
      missingSelections,
    };
  }

  const selectedPlan = input.plans.find((plan) => String(plan.id) === String(input.planId));
  const hasValidQuantity = Number.isInteger(input.bagCount) && input.bagCount >= 1 && input.bagCount <= 4;
  if (!hasValidQuantity) missingSelections.push('quantity');
  if (!selectedPlan) missingSelections.push('frequency');

  const quantity = hasValidQuantity ? input.bagCount : 0;
  const deliveryPrices = {
    1: selectedPlan?.oneBagPrice,
    2: selectedPlan?.twoBagPrice,
    3: selectedPlan?.threeBagPrice,
    4: selectedPlan?.fourBagPrice,
  };
  const deliveryPrice = deliveryPrices[quantity] || input.oncePrice;
  const savingPercent = Number(selectedPlan?.savingPercent || 0);

  return {
    sellingPlanId: selectedPlan?.id || '',
    quantity,
    buttonText:
      missingSelections.length > 0
        ? 'Complete your subscription choices'
        : savingPercent > 0
          ? `Subscribe and save ${savingPercent}%`
          : 'Add subscription to cart',
    deliveryPrice,
    savingPercent,
    isComplete: missingSelections.length === 0,
    missingSelections,
  };
}

/**
 * Ensures Shopify receives one unambiguous selling plan value.
 *
 * @param {FormData} formData
 * @param {string} sellingPlanId
 */
export function applyBbxSellingPlanToFormData(formData, sellingPlanId) {
  if (sellingPlanId) {
    formData.set('selling_plan', sellingPlanId);
  } else {
    formData.delete('selling_plan');
  }

  return formData;
}

/**
 * Keeps Shopify's selling plan field completely out of one time forms.
 *
 * @param {{value: string, disabled: boolean}} input
 * @param {{purchaseType: 'once' | 'subscribe', isComplete: boolean, sellingPlanId: string}} state
 */
export function setBbxSellingPlanInputState(input, state) {
  const hasActivePlan = state.purchaseType === 'subscribe' && state.isComplete && Boolean(state.sellingPlanId);
  input.value = hasActivePlan ? state.sellingPlanId : '';
  input.disabled = !hasActivePlan;
}

/**
 * Builds one Shopify Ajax Cart line for a queued product form submission.
 *
 * @param {{variantId: string, quantity: number, sellingPlanId?: string}} item
 */
export function buildBbxCartLine(item) {
  const line = {
    id: Number(item.variantId),
    quantity: item.quantity,
  };

  if (item.sellingPlanId) {
    line.selling_plan = Number(item.sellingPlanId);
  }

  return line;
}

/**
 * Finds the shared product details area that contains both the variant picker
 * and the product form. The variant picker is a sibling of the form in Horizon,
 * so observing the form alone misses grind changes.
 *
 * @param {{closest: (selector: string) => EventTarget | null}} element
 * @returns {EventTarget | null}
 */
export function findBbxProductChoiceRoot(element) {
  return element.closest('.product-details') || element.closest('product-form-component');
}

/**
 * Reads the real selected Grind control from Shopify's variant picker.
 * This keeps the custom selector aligned with the variant that will be added.
 *
 * @param {{querySelectorAll?: (selector: string) => Iterable<HTMLElement>}} root
 * @returns {string}
 */
export function readBbxSelectedGrind(root) {
  const optionControls = Array.from(root?.querySelectorAll?.('[data-option-name]') || []);
  const selectedGrind = optionControls.find((control) => {
    if (String(control.dataset?.optionName || '').toLowerCase() !== 'grind') return false;
    if ('checked' in control) return Boolean(control.checked);
    if ('selected' in control) return Boolean(control.selected);
    return false;
  });

  return String(selectedGrind?.value || '');
}

/**
 * Chooses the actual fortnightly plan by meaning, rather than array position.
 *
 * @param {Array<{id: string, name: string}>} plans
 * @returns {{id: string, name: string} | undefined}
 */
export function findBbxTwoWeekPlan(plans) {
  return plans.find((plan) => {
    const name = String(plan?.name || '');
    return /\bevery\s+2\s+weeks?\b/i.test(name) || /\bfortnight(?:ly)?\b/i.test(name);
  });
}

/**
 * Establishes the requested defaults from real Shopify controls and plans.
 *
 * @param {{selectedGrind: string, plans: Array<{id: string, name: string}>}} input
 */
export function deriveBbxInitialSubscriptionDefaults(input) {
  return {
    grindConfirmed: String(input.selectedGrind || '').trim().length > 0,
    planId: String(findBbxTwoWeekPlan(input.plans)?.id || ''),
  };
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class BbxSubscriptionSelector extends HTMLElementBase {
  /** @type {Record<string, {oncePrice: string, plans: Array<{id: string, name: string, oneBagPrice: string, twoBagPrice: string, threeBagPrice: string, fourBagPrice: string, savingPercent: number}>}>} */
  #variantData = {};
  #grindConfirmed = false;
  #productChoiceRoot = null;
  #lastPublishedCompletion = '';
  #hasPublishedStart = false;

  connectedCallback() {
    const dataElement = this.querySelector('[data-bbx-subscription-data]');
    this.#variantData = JSON.parse(dataElement?.textContent || '{}');
    this.addEventListener('change', this.#handleChange);
    this.#productChoiceRoot = findBbxProductChoiceRoot(this);
    this.#productChoiceRoot?.addEventListener('click', this.#handleVariantChoice);
    this.#productChoiceRoot?.addEventListener('change', this.#handleVariantChoice);
    this.#isolateVisualControls();
    const entryUrl = new URL(globalThis.location.href);
    const requested = entryUrl.searchParams.get('purchase');
    const requestedPlan = entryUrl.searchParams.get('selling_plan');
    const intent = requested === 'subscribe' || (!requested && (requestedPlan || globalThis.location.hash === '#choose-subscription')) ? 'subscribe' : 'once';
    if (intent === 'subscribe' && requestedPlan && this.#variantData[this.dataset.bbxVariantId]?.plans.some(plan => String(plan.id) === requestedPlan)) {
      this.querySelectorAll('[data-bbx-control="bbx-frequency"]').forEach(input => { input.checked = input.value === requestedPlan; });
    }
    this.querySelectorAll('[data-bbx-control="bbx-purchase-type"]').forEach(input => { input.checked = input.value === intent; });
    this.#syncInitialDefaults();
    this.#applyState();
    globalThis.queueMicrotask?.(() => {
      this.#isolateVisualControls();
      this.#syncInitialDefaults();
      this.#applyState();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#productChoiceRoot?.removeEventListener('click', this.#handleVariantChoice);
    this.#productChoiceRoot?.removeEventListener('change', this.#handleVariantChoice);
  }

  /** @param {string | number} variantId */
  setVariant(variantId) {
    const nextVariant = this.#variantData[String(variantId)];
    if (!nextVariant) {
      this.hidden = true;
      const planInput = this.querySelector('[name="selling_plan"]');
      if (planInput) { planInput.disabled = true; planInput.value = ''; }
      return;
    }
    this.hidden = false;

    this.#isolateVisualControls();
    const frequencyInputs = Array.from(this.querySelectorAll('[data-bbx-control="bbx-frequency"]'));
    const selectedPlanId = frequencyInputs.find((input) => input.checked)?.value || '';
    const retainedPlan = nextVariant.plans.find((plan) => String(plan.id) === String(selectedPlanId));
    const nextSelectedPlanId = retainedPlan?.id || findBbxTwoWeekPlan(nextVariant.plans)?.id || '';

    this.dataset.bbxVariantId = String(variantId);
    frequencyInputs.forEach((input, index) => {
      const nextPlan = nextVariant.plans[index];
      input.value = nextPlan?.id || '';
      input.disabled = !nextPlan;
      input.checked = Boolean(nextSelectedPlanId) && String(nextPlan?.id) === String(nextSelectedPlanId);
      const label = input.closest('.bbx-subscription__frequency')?.querySelector('span');
      if (label && nextPlan) label.textContent = nextPlan.name;
    });

    this.querySelectorAll('[data-bbx-once-price]').forEach((price) => {
      price.textContent = nextVariant.oncePrice;
    });

    const subscribeInput = this.querySelector('[data-bbx-control="bbx-purchase-type"][value="subscribe"]');
    const onceInput = this.querySelector('[data-bbx-control="bbx-purchase-type"][value="once"]');
    const hasPlans = nextVariant.plans.length > 0;
    if (subscribeInput) subscribeInput.disabled = !hasPlans;
    // Preserve subscription intent. An unavailable plan requires an explicit new choice.

    this.#syncInitialDefaults();
    this.#applyState();
  }

  #syncInitialDefaults() {
    const variant = this.#variantData[this.dataset.bbxVariantId || ''];
    if (!variant) return;

    const defaults = deriveBbxInitialSubscriptionDefaults({
      selectedGrind: readBbxSelectedGrind(this.#productChoiceRoot),
      plans: variant.plans,
    });
    this.#grindConfirmed = defaults.grindConfirmed;
    if (this.#grindConfirmed) {
      this.dataset.bbxGrindConfirmed = 'true';
    } else {
      delete this.dataset.bbxGrindConfirmed;
    }

    const frequencyInputs = Array.from(this.querySelectorAll('[data-bbx-control="bbx-frequency"]'));
    if (!frequencyInputs.some((input) => input.checked) && defaults.planId) {
      const defaultFrequency = frequencyInputs.find((input) => String(input.value) === defaults.planId);
      if (defaultFrequency && !defaultFrequency.disabled) defaultFrequency.checked = true;
    }
  }

  #handleChange = (event) => {
    this.#isolateVisualControls();
    const changedControl = event.target?.closest?.('[data-bbx-control]');
    const feedback = this.closest('product-form-component')?.querySelector('[data-bbx-form-feedback]');
    if (feedback) feedback.hidden = true;
    if (changedControl?.dataset.bbxControl === 'bbx-purchase-type') {
      const url = new URL(globalThis.location.href);
      url.searchParams.set('purchase', changedControl.value);
      globalThis.history.replaceState(globalThis.history.state, '', url);
    }
    if (changedControl?.checked) {
      this.querySelectorAll(`[data-bbx-control="${changedControl.dataset.bbxControl}"]`).forEach((control) => {
        if (control !== changedControl) control.checked = false;
      });
    }
    const purchaseType =
      this.querySelector('[data-bbx-control="bbx-purchase-type"]:checked')?.value === 'once' ? 'once' : 'subscribe';
    this.#publishStart(purchaseType);
    const choiceUrl = new URL(globalThis.location.href);
    choiceUrl.searchParams.set('purchase', purchaseType);
    choiceUrl.searchParams.delete('selling_plan');
    globalThis.history.replaceState(globalThis.history.state, '', choiceUrl);
    this.#applyState();
  };

  #handleVariantChoice = (event) => {
    const target = event.target;
    const option = target?.matches?.('[data-option-name]')
      ? target
      : target?.selectedOptions?.[0];
    const optionName = option?.dataset?.optionName || '';
    if (optionName.toLowerCase() !== 'grind') return;

    this.#grindConfirmed = true;
    this.dataset.bbxGrindConfirmed = 'true';
    this.#publishStart(
      this.querySelector('[data-bbx-control="bbx-purchase-type"]:checked')?.value === 'once' ? 'once' : 'subscribe'
    );
    this.#applyState();
  };

  #publishStart(purchaseType) {
    if (this.#hasPublishedStart) return;
    const productForm = this.closest('product-form-component');
    globalThis.BobbysAnalytics?.publish?.('bb_subscription_selector_started', {
      product_id: productForm?.dataset.productId,
      variant_id: this.dataset.bbxVariantId,
      purchase_type: purchaseType,
    });
    this.#hasPublishedStart = true;
  }

  #isolateVisualControls() {
    this.querySelectorAll('[name^="bbx-"]').forEach((control) => {
      control.dataset.bbxControl = control.getAttribute('name');
      control.removeAttribute('name');
    });
  }

  #applyState() {
    const form = this.closest('form');
    const variantId = this.dataset.bbxVariantId || '';
    if (!this.#variantData[variantId]) {
      const dataElement = this.querySelector('[data-bbx-subscription-data]');
      this.#variantData = JSON.parse(dataElement?.textContent || '{}');
    }
    const variant = this.#variantData[variantId];
    if (!form || !variant) return;

    const purchaseType =
      this.querySelector('[data-bbx-control="bbx-purchase-type"]:checked')?.value === 'subscribe'
        ? 'subscribe'
        : 'once';
    const bagCount = Number(this.querySelector('[data-bbx-control="bbx-bag-count"]:checked')?.value || 0);
    const planId = this.querySelector('[data-bbx-control="bbx-frequency"]:checked')?.value || '';
    const state = deriveSubscriptionState({
      purchaseType,
      grindConfirmed: this.#grindConfirmed,
      bagCount,
      planId,
      oncePrice: variant.oncePrice,
      onceTwoPrice: variant.onceTwoPrice,
      onceThreePrice: variant.onceThreePrice,
      onceFourPrice: variant.onceFourPrice,
      plans: variant.plans,
    });

    const selectedPlan = variant.plans.find((plan) => String(plan.id) === String(planId));
    this.querySelectorAll('[data-bbx-subscription-price]').forEach((price) => {
      price.textContent = selectedPlan?.oneBagPrice || variant.oncePrice;
    });
    this.querySelectorAll('[data-bbx-two-bag-price]').forEach((price) => {
      price.textContent = selectedPlan?.twoBagPrice || variant.oncePrice;
    });
    this.querySelectorAll('[data-bbx-three-bag-price]').forEach((price) => {
      price.textContent = selectedPlan?.threeBagPrice || variant.oncePrice;
    });
    this.querySelectorAll('[data-bbx-four-bag-price]').forEach((price) => {
      price.textContent = selectedPlan?.fourBagPrice || variant.oncePrice;
    });

    const savingBadge = this.querySelector('[data-bbx-saving-badge]');
    if (savingBadge) {
      savingBadge.hidden = state.savingPercent <= 0;
      savingBadge.textContent = state.savingPercent > 0 ? `Save ${state.savingPercent}%` : '';
    }

    updateCoffeeShipping(this, variant.weight, state.quantity, purchaseType);
    this.dataset.purchaseType = purchaseType;
    const onceSummary = this.querySelector('[data-bbx-once-summary]');
    if (onceSummary) {
      onceSummary.hidden = purchaseType !== 'once';
      onceSummary.querySelector('[data-bbx-once-total]').textContent = state.deliveryPrice;
    }
    const panel = this.querySelector('[data-bbx-subscription-panel]');
    if (panel) panel.hidden = purchaseType !== 'subscribe';

    const prompt = this.querySelector('[data-bbx-selection-prompt]');
    if (prompt) {
      prompt.textContent = state.isComplete
        ? purchaseType === 'subscribe'
          ? 'Ready: your grind, quantity and delivery frequency are confirmed.'
          : 'Ready: your grind is confirmed.'
        : `Still to choose: ${state.missingSelections.join(', ')}.`;
      prompt.dataset.complete = state.isComplete ? 'true' : 'false';
    }

    const recurringSummary = this.querySelector('[data-bbx-recurring-summary]');
    if (recurringSummary) {
      recurringSummary.hidden = purchaseType !== 'subscribe' || !state.isComplete;
      const recurringPrice = recurringSummary.querySelector('[data-bbx-recurring-price]');
      const recurringFrequency = recurringSummary.querySelector('[data-bbx-recurring-frequency]');
      if (recurringPrice) recurringPrice.textContent = state.deliveryPrice;
      if (recurringFrequency) recurringFrequency.textContent = selectedPlan?.name || '';
    }

    const productForm = this.closest('product-form-component');
    if (productForm) {
      if (purchaseType === 'subscribe') {
        productForm.dataset.bbxSubscriptionActive = 'true';
      } else {
        delete productForm.dataset.bbxSubscriptionActive;
      }
    }

    const sellingPlanInput = this.querySelector('[name="selling_plan"]');
    if (sellingPlanInput) {
      setBbxSellingPlanInputState(sellingPlanInput, {
        purchaseType,
        isComplete: state.isComplete,
        sellingPlanId: state.sellingPlanId,
      });
    }

    const quantityInput = form.querySelector('[name="quantity"]');
    const quantitySelector = quantityInput?.closest('quantity-selector-component');
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(state.quantity);
    } else if (quantityInput) {
      quantityInput.value = String(state.quantity);
    }

    const buttonText = form.querySelector('[data-bbx-add-label], .add-to-cart-text__content span span');
    if (buttonText) buttonText.textContent = state.buttonText;

    form.querySelectorAll('button[name="add"]').forEach((button) => {
      if (!state.isComplete) {
        button.disabled = true;
        button.dataset.bbxChoiceDisabled = 'true';
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.bbxChoiceDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.bbxChoiceDisabled;
        button.removeAttribute('aria-disabled');
      }
    });

    if (purchaseType === 'subscribe' && state.isComplete) {
      const completionKey = [variantId, state.sellingPlanId, state.quantity].join(':');
      if (completionKey !== this.#lastPublishedCompletion) {
        globalThis.BobbysAnalytics?.publish?.('bb_subscription_selector_completed', {
          product_id: productForm?.dataset.productId,
          variant_id: variantId,
          selling_plan_id: state.sellingPlanId,
          quantity: state.quantity,
          frequency: selectedPlan?.name,
          delivery_price: state.deliveryPrice,
        });
        this.#lastPublishedCompletion = completionKey;
      }
    } else {
      this.#lastPublishedCompletion = '';
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('bbx-subscription-selector')) {
  globalThis.customElements.define('bbx-subscription-selector', BbxSubscriptionSelector);
}
